import { useState } from 'react';
import { api, sizeLabel, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';
import NewItemButton from '../components/NewItemButton';

// Bundles: several items sold together at one price, like a gift pack or a party combo. It rings up as one line; each item comes out of stock.
export default function Bundles() {
  const list = useLoad(() => api<any[]>('/api/bundles'), []);
  const [adding, setAdding] = useState(false);
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Bundles</h1><p>Sell items together at one price. The bundle gets its own barcode and shelf price; the items inside leave stock when it sells, and their cost counts toward your margin.</p></div>
        <button className="primary" onClick={() => setAdding(true)}>New bundle</button>
      </div>
      <ErrorNote error={list.error} />
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Bundle</th><th>What is in it</th><th className="n">Price</th><th className="n">Can make</th><th>Barcode</th></tr></thead>
          <tbody>{list.data?.map((b) => (
            <tr key={b.id} style={b.active ? undefined : { opacity: 0.5 }}>
              <td style={{ fontWeight: 600 }}>{b.name}</td>
              <td>{b.components.map((c: any) => `${c.qty} × ${c.brand} ${c.name} ${sizeLabel(c.size_ml)}`).join(', ')}</td>
              <td className="n">{usd(b.retail_cents)}</td>
              <td className="n"><span className={`chip ${b.available > 0 ? 'good' : 'bad'}`}>{b.available}</span></td>
              <td className="num">{b.code ?? '—'}</td>
            </tr>))}</tbody>
        </table>
        {list.data?.length === 0 && <div className="empty">No bundles yet.</div>}
      </div></div>
      {adding && <NewBundle onClose={() => setAdding(false)} onDone={() => { setAdding(false); list.reload(); }} />}
    </div>
  );
}

function NewBundle({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [barcode, setBarcode] = useState('');
  const [find, setFind] = useState('');
  const found = useLoad(() => (find.length >= 2 ? api<any[]>(`/api/items?q=${encodeURIComponent(find)}&limit=8`) : Promise.resolve([])), [find]);
  const [lines, setLines] = useState<{ item: any; qty: string }[]>([]);
  const [err, setErr] = useState<unknown>(null);
  const cents = Math.round(Number(price || '0') * 100);
  const cost = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (l.item.last_unit_cost_cents ?? 0), 0);
  const retail = lines.reduce((s, l) => s + (Number(l.qty) || 0) * l.item.retail_cents, 0);
  const ok = name.trim().length >= 2 && cents > 0 && lines.length > 0 && lines.every((l) => Number(l.qty) > 0) && cents >= cost;
  return (
    <Modal title="New bundle" onClose={onClose} wide footer={<button className="primary" disabled={!ok} onClick={async () => {
      try { await api('/api/bundles', { body: { name, priceCents: cents, barcode: barcode || null, lines: lines.map((l) => ({ itemId: l.item.id, qty: Number(l.qty) })) } }); onDone(); } catch (e) { setErr(e); }
    }}>Create bundle</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Name<input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Game day pack" /></label>
        <label>Price ($)<input className="num-input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0.00" /></label>
        <label>Barcode (optional)<input inputMode="numeric" value={barcode} onChange={(e) => setBarcode(e.target.value.replace(/\D/g, ''))} /></label>
      </div>
      <label>Add an item<input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search your items" /></label>
      {found.data?.filter((i) => !lines.some((l) => l.item.id === i.id) && !i.is_bundle).map((i) => (
        <button key={i.id} className="ghost" style={{ display: 'block', width: '100%', textAlign: 'left' }} onClick={() => { setLines([...lines, { item: i, qty: '1' }]); setFind(''); }}>{i.brand} {i.name} · {sizeLabel(i.size_ml)} · {usd(i.retail_cents)}</button>))}
      <div><NewItemButton onCreated={(i) => { setLines([...lines, { item: i, qty: '1' }]); setFind(''); }} /></div>
      {lines.length > 0 && (
        <table className="grid"><tbody>{lines.map((l, i) => (
          <tr key={l.item.id}>
            <td>{l.item.brand} {l.item.name} <span className="faint">{sizeLabel(l.item.size_ml)} · {usd(l.item.retail_cents)}</span></td>
            <td className="n"><input style={{ width: 64, textAlign: 'right' }} inputMode="numeric" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value.replace(/\D/g, '') } : x)))} /></td>
            <td><button className="ghost danger" onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button></td>
          </tr>))}</tbody></table>
      )}
      {lines.length > 0 && <p className="faint">Items sold separately: {usd(retail)} · your cost: {usd(cost)}{cents > 0 && cents < cost ? ' · the price is under cost, which state liquor law does not allow' : cents > 0 ? ` · you save the customer ${usd(Math.max(0, retail - cents))}` : ''}</p>}
    </Modal>
  );
}
